import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useDebouncedCallback } from 'use-debounce';

import { config } from '../../api/config';
import { Api } from '../../api/api';
import { actions } from '../../state';
import { formatMoneyToCents, getTimestamp } from '../../utils';
import {
  Header,
  Breadcrumb,
  Paper,
  PaperHeader,
  TitleSecondary,
} from '../../components';
import Form from './physicalCards.form';
import {
  buildMembershipOptions,
  isExactCardMatch,
  MEMBERSHIPS_FETCH_PARAMS,
  MIN_OWNER_SEARCH_LENGTH,
  OWNER_TYPE_PRIVATE,
  OWNER_TYPE_OPTIONS,
  searchOwnersByType,
  STATUS_OPTIONS,
} from './physicalCards.helpers';
import {
  DUPLICATE_CARD_MESSAGE,
  DISCOUNT_RANGE_ERROR,
  hasValidationErrors,
  validatePhysicalCardForm,
} from './physicalCards.validation';

const AddPhysicalCard = ({
  dispatch,
  history,
  physicalCards: { loading },
  memberships: { memberships },
}) => {
  const [values, setValues] = useState({
    number: '',
    owner_type: OWNER_TYPE_PRIVATE,
    owner_id: null,
    ownerQuery: '',
    membership_id: '',
    discount_valid_from: '',
    discount_valid_from_time: '',
    discount_valid_to: '',
    discount_valid_to_time: '',
    initial_balance: '0',
    status: 1,
  });
  const [errors, setErrors] = useState({});
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [ownerResults, setOwnerResults] = useState([]);
  const [ownerSearchLoading, setOwnerSearchLoading] = useState(false);
  const [ownerSearchError, setOwnerSearchError] = useState('');

  const fetchMemberships = useCallback(() => {
    dispatch(
      actions.memberships.getMembershipsAction(MEMBERSHIPS_FETCH_PARAMS)
    );
  }, [dispatch]);

  const clearMemberships = useCallback(() => {
    dispatch(actions.memberships.clearMembershipsAction());
  }, [dispatch]);

  useEffect(() => {
    fetchMemberships();
    return () => clearMemberships();
  }, [fetchMemberships, clearMemberships]);

  const membershipOptions = useMemo(() => buildMembershipOptions(memberships), [
    memberships,
  ]);

  const formValues = useMemo(
    () => ({
      ...values,
      statusOptions: STATUS_OPTIONS,
      ownerTypeOptions: OWNER_TYPE_OPTIONS,
      membershipOptions,
    }),
    [values, membershipOptions]
  );

  const liveErrors = useMemo(
    () => validatePhysicalCardForm(values, { isDuplicate, mode: 'create' }),
    [values, isDuplicate]
  );

  const isSubmitDisabled = useMemo(() => hasValidationErrors(liveErrors), [
    liveErrors,
  ]);

  const displayErrors = useMemo(() => {
    const next = { ...errors };

    if (liveErrors.discount_valid_to) {
      next.discount_valid_to = liveErrors.discount_valid_to;
    } else if (next.discount_valid_to === DISCOUNT_RANGE_ERROR) {
      delete next.discount_valid_to;
    }

    return next;
  }, [errors, liveErrors]);

  const checkDuplicateNumber = useCallback(async number => {
    const trimmed = (number || '').trim();

    if (trimmed.length < MIN_OWNER_SEARCH_LENGTH) {
      setIsDuplicate(false);
      setErrors(prev => {
        if (prev.number !== DUPLICATE_CARD_MESSAGE) {
          return prev;
        }
        const next = { ...prev };
        delete next.number;
        return next;
      });
      return false;
    }

    try {
      const results = await Api.searchPhysicalCards(trimmed);
      const duplicate = isExactCardMatch(results, trimmed);
      setIsDuplicate(duplicate);

      setErrors(prev => {
        if (duplicate) {
          return { ...prev, number: DUPLICATE_CARD_MESSAGE };
        }
        if (prev.number === DUPLICATE_CARD_MESSAGE) {
          const next = { ...prev };
          delete next.number;
          return next;
        }
        return prev;
      });

      return duplicate;
    } catch (e) {
      return false;
    }
  }, []);

  const debouncedDuplicateCheck = useDebouncedCallback(number => {
    checkDuplicateNumber(number);
  }, 800);

  const debouncedOwnerSearch = useDebouncedCallback(
    async (query, ownerType) => {
      const trimmed = (query || '').trim();

      if (trimmed.length < MIN_OWNER_SEARCH_LENGTH) {
        setOwnerResults([]);
        setOwnerSearchError('');
        setOwnerSearchLoading(false);
        return;
      }

      setOwnerSearchLoading(true);
      setOwnerSearchError('');

      try {
        const results = await searchOwnersByType(trimmed, ownerType);
        setOwnerResults(results);
      } catch (e) {
        setOwnerResults([]);
        setOwnerSearchError(e.error ? e.error.error : e.message || 'Klaida');
      } finally {
        setOwnerSearchLoading(false);
      }
    },
    800
  );

  const handleInputChange = ({ target: { name, value } }) => {
    if (name === 'owner_type') {
      const ownerType = Number(value);
      setValues(prev => ({
        ...prev,
        owner_type: ownerType,
        owner_id: null,
        ownerQuery: '',
      }));
      setOwnerResults([]);
      setOwnerSearchError('');
      setErrors(prev => {
        const next = { ...prev };
        delete next.owner;
        delete next.owner_type;
        return next;
      });
      return;
    }

    if (name === 'membership_id') {
      const membershipId = value === '' ? '' : Number(value);
      setValues(prev => ({
        ...prev,
        membership_id: membershipId,
        ...(membershipId
          ? {}
          : {
              discount_valid_from: '',
              discount_valid_from_time: '',
              discount_valid_to: '',
              discount_valid_to_time: '',
            }),
      }));
      setErrors(prev => {
        const next = { ...prev };
        delete next.membership_id;
        delete next.discount_valid_from;
        delete next.discount_valid_to;
        return next;
      });
      return;
    }

    if (name === 'discount_valid_to') {
      setValues(prev => ({
        ...prev,
        discount_valid_to: value,
        ...(value ? {} : { discount_valid_to_time: '' }),
      }));
      setErrors(prev => {
        const next = { ...prev };
        delete next.discount_valid_to;
        return next;
      });
      return;
    }

    if (name === 'status') {
      setValues(prev => ({ ...prev, status: Number(value) }));
      setErrors(prev => {
        const next = { ...prev };
        delete next.status;
        return next;
      });
      return;
    }

    if (name === 'number') {
      setValues(prev => ({ ...prev, number: value }));
      setIsDuplicate(false);
      setErrors(prev => {
        const next = { ...prev };
        delete next.number;
        return next;
      });
      debouncedDuplicateCheck(value);
      return;
    }

    setValues(prev => ({ ...prev, [name]: value }));
    setErrors(prev => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleOwnerQueryChange = ({ target: { value } }) => {
    setValues(prev => ({
      ...prev,
      ownerQuery: value,
      owner_id: null,
    }));
    setErrors(prev => {
      const next = { ...prev };
      delete next.owner;
      return next;
    });
    debouncedOwnerSearch(value, values.owner_type);
  };

  const handleOwnerSelect = ownerId => {
    const selected = ownerResults.find(
      result => String(result.value) === String(ownerId)
    );

    setValues(prev => ({
      ...prev,
      owner_id: Number(ownerId),
      ownerQuery: selected ? selected.label : prev.ownerQuery,
    }));
    setOwnerResults([]);
    setErrors(prev => {
      const next = { ...prev };
      delete next.owner;
      return next;
    });
  };

  const buildSubmitBody = formState => {
    const body = {
      number: formState.number.trim(),
      owner_type: Number(formState.owner_type),
      owner_id: Number(formState.owner_id),
      status: Number(formState.status),
      initial_balance: formatMoneyToCents(formState.initial_balance || 0),
    };

    if (formState.membership_id) {
      body.membership_id = Number(formState.membership_id);
      body.discount_valid_from = getTimestamp(
        formState.discount_valid_from,
        formState.discount_valid_from_time || '00:00'
      );
      body.discount_valid_to = formState.discount_valid_to
        ? getTimestamp(
            formState.discount_valid_to,
            formState.discount_valid_to_time || '23:59'
          )
        : null;
    }

    return body;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const duplicate = await checkDuplicateNumber(values.number);
    const nextErrors = validatePhysicalCardForm(values, {
      isDuplicate: duplicate,
      mode: 'create',
    });

    setErrors(nextErrors);

    if (hasValidationErrors(nextErrors)) {
      return;
    }

    dispatch(
      actions.physicalCards.addPhysicalCardAction(
        buildSubmitBody(values),
        history
      )
    );
  };

  return (
    <section>
      <Header size="sm">
        <Breadcrumb
          title={config.PAGES.physicalCards}
          baseUrl={config.APP_PATHS.physicalCards}
          variant="pageTitle"
        />
      </Header>

      <Paper size="sm">
        <PaperHeader>
          <TitleSecondary>{config.PAGES.addPhysicalCard}</TitleSecondary>
        </PaperHeader>
        <Form
          mode="create"
          values={formValues}
          errors={displayErrors}
          onChange={handleInputChange}
          onOwnerQueryChange={handleOwnerQueryChange}
          onOwnerSelect={handleOwnerSelect}
          onSubmit={handleSubmit}
          loading={loading}
          submitDisabled={isSubmitDisabled}
          ownerResults={ownerResults}
          ownerSearchLoading={ownerSearchLoading}
          ownerSearchError={ownerSearchError}
        />
      </Paper>
    </section>
  );
};

const mapStateToProps = state => ({
  physicalCards: state.physicalCards,
  memberships: state.memberships,
});

AddPhysicalCard.propTypes = {
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  physicalCards: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
  }).isRequired,
  memberships: PropTypes.shape({
    memberships: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        title_lt: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
};

export default connect(mapStateToProps)(AddPhysicalCard);
